import { serializeParameters, deserializeParameters } from "./sessionParameters";
import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { GitCompare, Loader2, Pencil, Plus, Save, Trash2, Type } from "lucide-react";
import { API_BASE } from "../../shared/Constants";
import { usePlayground } from "../../contexts/PlaygroundContext";
import { SavedSession, PlaygroundMode } from "./types";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { reportApiError, reportHandledError } from "../../utils/reportError";

interface PlaygroundSessionManagerProps {
    disabled?: boolean;
    onSessionChange?: (sessionId: string | null) => void;
}

export interface PlaygroundSessionManagerRef {
    clearSession: () => void;
}

const PlaygroundSessionManager = forwardRef<PlaygroundSessionManagerRef, PlaygroundSessionManagerProps>(
    function PlaygroundSessionManager({ disabled, onSessionChange }, ref) {
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const { state, dispatch } = usePlayground();

    const [sessions, setSessions] = useState<SavedSession[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [sessionName, setSessionName] = useState("");
    const [editingSession, setEditingSession] = useState<SavedSession | null>(null);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
        clearSession: () => { setCurrentSessionId(null); },
    }), []);

    const fetchSessions = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!token) return;
            const response = await fetch(`${API_BASE}/api/playground/sessions?limit=50`, {
                headers: { Authorization: `Auth ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setSessions(data.data || []);
            } else {
                reportApiError(response, { feature: "playground.session.list", method: "GET" });
            }
        } catch (error) {
            reportHandledError(error, { category: "api", feature: "playground.session.list" });
            console.error("Error fetching sessions:", error);
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently, loginWithRedirect]);

    useEffect(() => { fetchSessions(); }, [fetchSessions]);

    const handleSaveClick = () => {
        setEditingSession(null);
        setSessionName("");
        setSaveDialogOpen(true);
    };

    const handleSessionSelect = (sessionId: string) => {
        if (!sessionId) return;
        if (sessionId === "__new__") {
            handleNewSession();
            return;
        }
        const session = sessions.find(s => s.id === sessionId);
        if (session) handleLoadSession(session);
    };

    const handleNewSession = () => {
        dispatch({ type: 'RESET_ALL' });
        setCurrentSessionId(null);
        onSessionChange?.(null);
    };

    const handleSaveSession = async () => {
        if (!sessionName.trim()) return;

        try {
            setIsLoading(true);
            const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!token) return;

            const sessionData = {
                name: sessionName.trim(),
                mode: state.mode,
                selected_models: state.selectedModels,
                system_prompts: state.systemPrompts,
                user_messages: state.userMessages,
                parameters: serializeParameters(state),
                responses: Object.fromEntries(
                    Object.entries(state.responses).map(([key, response]) => [
                        key,
                        {
                            content: response.content,
                            token_count: {
                                input: response.tokenCount.input,
                                output: response.tokenCount.output,
                            },
                        },
                    ])
                ),
            };

            const url = editingSession
                ? `${API_BASE}/api/playground/sessions/${editingSession.id}`
                : `${API_BASE}/api/playground/sessions`;

            const method = editingSession ? "PATCH" : "POST";

            const response = await fetch(url, {
                method,
                headers: {
                    Authorization: `Auth ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(sessionData),
            });

            if (response.ok) {
                const savedSession = await response.json();
                setCurrentSessionId(savedSession.id);
                onSessionChange?.(savedSession.id);
                await fetchSessions();
                setSaveDialogOpen(false);
            } else {
                reportApiError(response, { feature: "playground.session.save", method });
            }
        } catch (error) {
            reportHandledError(error, { category: "api", feature: "playground.session.save" });
            console.error("Error saving session:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadSession = (session: SavedSession) => {
        const userMessages = session.user_messages && session.user_messages.length > 0
            ? [...session.user_messages]
            : [''];

        const systemPrompts = session.system_prompts && session.system_prompts.length > 0
            ? [...session.system_prompts]
            : [''];

        while (systemPrompts.length < userMessages.length) {
            systemPrompts.push('');
        }

        const responses: Record<string, { content: string; tokenCount: { input: number; output: number } }> = {};

        if (session.responses) {
            Object.entries(session.responses).forEach(([key, response]) => {
                responses[key] = {
                    content: response.content,
                    tokenCount: {
                        input: response.token_count?.input || 0,
                        output: response.token_count?.output || 0,
                    },
                };
            });
        }

        dispatch({
            type: 'LOAD_SESSION',
            session: {
                mode: session.mode || 'compare-models',
                selectedModels: session.selected_models || [],
                systemPrompts,
                userMessages,
                ...deserializeParameters(session.parameters),
                responses,
            },
        });

        setCurrentSessionId(session.id);
        onSessionChange?.(session.id);
    };

    const handleEditSession = (session: SavedSession, event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setEditingSession(session);
        setSessionName(session.name);
        setSaveDialogOpen(true);
    };

    const handleDeleteSession = async (session: SavedSession, event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (!window.confirm(`Excluir sessão "${session.name}"?`)) return;

        try {
            const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!token) return;
            const response = await fetch(`${API_BASE}/api/playground/sessions/${session.id}`, {
                method: "DELETE",
                headers: { Authorization: `Auth ${token}` },
            });

            if (response.ok) {
                if (currentSessionId === session.id) {
                    setCurrentSessionId(null);
                    onSessionChange?.(null);
                }
                await fetchSessions();
            } else {
                reportApiError(response, { feature: "playground.session.delete", method: "DELETE" });
            }
        } catch (error) {
            reportHandledError(error, { category: "api", feature: "playground.session.delete" });
            console.error("Error deleting session:", error);
        }
    };

    const handleUpdateCurrentSession = async () => {
        if (!currentSessionId) {
            handleSaveClick();
            return;
        }

        try {
            setIsLoading(true);
            const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
            if (!token) return;

            const sessionData = {
                mode: state.mode,
                selected_models: state.selectedModels,
                system_prompts: state.systemPrompts,
                user_messages: state.userMessages,
                parameters: serializeParameters(state),
                responses: Object.fromEntries(
                    Object.entries(state.responses).map(([key, response]) => [
                        key,
                        {
                            content: response.content,
                            token_count: {
                                input: response.tokenCount.input,
                                output: response.tokenCount.output,
                            },
                        },
                    ])
                ),
            };

            await fetch(`${API_BASE}/api/playground/sessions/${currentSessionId}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Auth ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(sessionData),
            });

            await fetchSessions();
        } catch (error) {
            reportHandledError(error, { category: "api", feature: "playground.session.update" });
            console.error("Error updating session:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString("pt-BR", {
            day: "2-digit", month: "2-digit", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    };

    const currentSession = sessions.find(s => s.id === currentSessionId);
    const currentModeSessions = sessions.filter(s => (s.mode || 'compare-models') === state.mode);
    const otherModeSessions = sessions.filter(s => (s.mode || 'compare-models') !== state.mode);

    const renderModeIcon = (mode: PlaygroundMode | undefined) => {
        if (mode === 'compare-prompts') {
            return <Type className="h-3.5 w-3.5 opacity-60" />;
        }
        return <GitCompare className="h-3.5 w-3.5 opacity-60" />;
    };

    return (
        <>
            <TooltipProvider>
                <div className="flex items-center gap-2">
                    <Select value={currentSessionId || ""} onValueChange={handleSessionSelect} disabled={disabled}>
                        <SelectTrigger className="h-9 min-w-[220px] text-xs">
                            <span className="truncate text-left">
                                {currentSession ? currentSession.name : "Selecionar sessão"}
                            </span>
                            <SelectValue className="hidden" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__new__" className="font-medium text-primary">
                                <span className="flex items-center gap-2">
                                    <Plus className="h-3.5 w-3.5" />
                                    Nova sessão
                                </span>
                            </SelectItem>

                            {currentModeSessions.length > 0 && (
                                <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wide">
                                        {state.mode === 'compare-models' ? 'Comparar Modelos' : 'Comparar Prompts'}
                                    </SelectLabel>
                                    {currentModeSessions.map((session) => (
                                        <SelectItem key={session.id} value={session.id} className="group">
                                            <div className="flex w-full items-center justify-between gap-2">
                                                <div className="flex min-w-0 flex-col">
                                                    <span className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                                                        {renderModeIcon(session.mode)}
                                                        {session.name}
                                                    </span>
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {formatDate(session.updated_at)}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleEditSession(session, e)}
                                                        aria-label={`Editar ${session.name}`}
                                                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                                                    >
                                                        <Pencil className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleDeleteSession(session, e)}
                                                        aria-label={`Excluir ${session.name}`}
                                                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            )}

                            {otherModeSessions.length > 0 && (
                                <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wide">
                                        {state.mode === 'compare-models' ? 'Comparar Prompts' : 'Comparar Modelos'}
                                    </SelectLabel>
                                    {otherModeSessions.map((session) => (
                                        <SelectItem key={session.id} value={session.id} className="group">
                                            <div className="flex w-full items-center justify-between gap-2">
                                                <div className="flex min-w-0 flex-col">
                                                    <span className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                                                        {renderModeIcon(session.mode)}
                                                        {session.name}
                                                    </span>
                                                    <span className="text-[11px] text-muted-foreground">
                                                        {formatDate(session.updated_at)}
                                                    </span>
                                                </div>
                                                <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleEditSession(session, e)}
                                                        aria-label={`Editar ${session.name}`}
                                                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                                                    >
                                                        <Pencil className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleDeleteSession(session, e)}
                                                        aria-label={`Excluir ${session.name}`}
                                                        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            )}

                            {sessions.length === 0 && (
                                <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                                    Nenhuma sessão salva
                                </div>
                            )}
                        </SelectContent>
                    </Select>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={handleUpdateCurrentSession}
                                disabled={disabled || isLoading}
                                aria-label={currentSessionId ? "Atualizar sessão atual" : "Salvar nova sessão"}
                            >
                                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {currentSessionId ? "Atualizar sessão atual" : "Salvar nova sessão"}
                        </TooltipContent>
                    </Tooltip>
                </div>
            </TooltipProvider>

            <Dialog open={saveDialogOpen} onOpenChange={(o) => !o && setSaveDialogOpen(false)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingSession ? "Renomear Sessão" : "Salvar Sessão"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-1.5">
                        <Label htmlFor="session-name">Nome da sessão</Label>
                        <Input
                            id="session-name"
                            autoFocus
                            value={sessionName}
                            onChange={(e) => setSessionName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveSession();
                            }}
                        />
                    </div>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSaveSession} disabled={!sessionName.trim() || isLoading}>
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
});

export default PlaygroundSessionManager;
